import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// --- Silent session refresh ---------------------------------------------------
// access_token cookie expires after 8h; a 7-day refresh_token cookie exists.
// On a mid-session 401 (for a DATA endpoint) we call /auth/refresh ONCE (deduped)
// and retry. /auth/me + the auth endpoints are excluded here — AuthContext owns
// the /auth/me refresh-on-load so we never get a reload/redirect loop.
let _refreshPromise = null;
let _onAuthLost = null;
// Lets AuthContext register a SOFT logout (sets user=false → React Router shows
// /login without a full-page reload). Avoids window.location reload churn.
export const setAuthLostHandler = (fn) => { _onAuthLost = fn; };

const _noRefresh = (url = "") =>
  url.includes("/auth/login") || url.includes("/auth/refresh") ||
  url.includes("/auth/logout") || url.includes("/auth/me");

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { response, config } = error;
    if (!response || response.status !== 401 || !config || config._retry || _noRefresh(config.url || "")) {
      return Promise.reject(error);
    }
    config._retry = true;
    try {
      if (!_refreshPromise) {
        _refreshPromise = api.post("/auth/refresh").finally(() => { _refreshPromise = null; });
      }
      await _refreshPromise;
      return api(config); // retry original request with the new access cookie
    } catch (e) {
      // Refresh token gone/expired → soft logout (no hard reload → no loading loop).
      if (_onAuthLost) _onAuthLost();
      return Promise.reject(error);
    }
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/**
 * Download an Excel/file response from an authenticated API endpoint and trigger
 * the browser "Save as" dialog. `path` is relative to API_BASE (e.g. "/export/students").
 */
export async function downloadFile(path, params = {}, fallbackName = "export.xlsx") {
  const res = await api.get(path, { params, responseType: "blob" });
  let filename = fallbackName;
  const cd = res.headers?.["content-disposition"] || "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  if (m) filename = m[1];
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default api;
